import { Button } from "@/components/ui/button";
import type { AppErrorType } from "@/lib/app-error-routes";
import { useAuth } from "@/providers/auth";

type AppErrorPageProps = {
  from?: string;
  layout?: "main" | "plain";
  type: AppErrorType;
};

type ErrorScreenCopy = {
  description: string;
  imageAlt: string;
  imageSrc: string;
  title: string;
};

const errorScreenCopy: Record<AppErrorType, ErrorScreenCopy> = {
  "404": {
    description: "page not found",
    imageAlt: "404 error illustration",
    imageSrc: "/error-screens/404.png",
    title: "Error 404",
  },
  "session-expired": {
    description: "please sign in again",
    imageAlt: "session expired illustration",
    imageSrc: "/error-screens/session-expired.png",
    title: "Session Expired",
  },
};

function getSafeAppHref(value?: string) {
  if (!value) {
    return undefined;
  }

  if (value.startsWith("/")) {
    return value;
  }

  if (typeof window === "undefined") {
    return undefined;
  }

  const url = new URL(value, window.location.origin);

  if (url.origin !== window.location.origin) {
    return undefined;
  }

  return `${url.pathname}${url.search}${url.hash}`;
}

function getPrimaryAction(
  type: AppErrorType,
  isLoggedIn: boolean,
  from?: string,
) {
  const safeFrom = getSafeAppHref(from);

  if (type === "session-expired" || !isLoggedIn) {
    if (safeFrom) {
      return {
        href: `/sign-in?redirect=${encodeURIComponent(safeFrom)}`,
        label: type === "session-expired" ? "Sign In Again" : "Sign In",
      };
    }

    return {
      href: "/sign-in",
      label: type === "session-expired" ? "Sign In Again" : "Sign In",
    };
  }

  return {
    href: "/",
    label: "Back Home",
  };
}

export function AppErrorPage({
  from,
  layout = "plain",
  type,
}: AppErrorPageProps) {
  const copy = errorScreenCopy[type];
  const { isAuthenticated, user } = useAuth();
  const action = getPrimaryAction(type, isAuthenticated || Boolean(user), from);

  return (
    <div
      className={`flex items-center justify-center bg-white px-4 py-10 sm:px-6 lg:px-8 ${layout === "main" ? "min-h-[calc(100svh-4rem)]" : "min-h-svh"}`}
    >
      <div className="mx-auto flex w-full max-w-[760px] flex-col items-center text-center">
        <img
          src={copy.imageSrc}
          alt={copy.imageAlt}
          className="w-full max-w-[560px] object-contain"
        />

        <h1 className="mt-2 font-medium text-[42px] text-brand-darkGreen leading-none sm:mt-6 sm:text-[58px]">
          {copy.title}
        </h1>

        <p className="mt-4 text-[#8f8f8f] text-[26px] leading-tight sm:text-[34px]">
          {copy.description}
        </p>

        <Button
          type="button"
          size="xl"
          className="mt-10 min-w-[196px] rounded-xl bg-black px-8 text-white text-xl hover:bg-black/90"
          onClick={() => {
            if (typeof window === "undefined") {
              return;
            }

            window.location.assign(action.href);
          }}
        >
          {action.label}
        </Button>
      </div>
    </div>
  );
}

export function RoutedAppErrorPage({ from, type }: Omit<AppErrorPageProps, "layout">) {
  const { isAuthenticated, user } = useAuth();

  return (
    <AppErrorPage
      type={type}
      from={from}
      layout={isAuthenticated || Boolean(user) ? "main" : "plain"}
    />
  );
}
